/**
 * Shared two-pane dimension / value editor used by the Filters popover
 * and the Manage saved filters dialog.
 */

import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import {
  FILTER_DIMENSION_KEYS,
  type FilterDimension,
  type FilterOption,
} from "../../../services/regulationFilterDimensionsService";
import {
  dimensionSelectionCount,
  isFilterOptionSelected,
  pinSelectedOptions,
  toggleFilterOption,
  type FilterSelectionState,
} from "./regulationFilterDraft";
import { DimensionChevronIcon, OptionCheckIcon } from "./filterPanelIcons";

const ACTIVE_BG = "rgba(41, 94, 241, 0.08)";
const ACTIVE_BAR = "#295EF1";

const customDateInputSx = {
  flex: "none",
  width: 129,
  minWidth: 129,
  maxWidth: 129,
  height: 32,
  boxSizing: "border-box",
  border: "none",
  boxShadow: "inset 0 0 0 1px #D9D9D9",
  borderRadius: "3px",
  px: "8px",
  fontSize: 13,
  lineHeight: "32px",
  fontFamily: "inherit",
  color: "#101828",
  bgcolor: "#fff",
  "&::-webkit-datetime-edit": {
    fontSize: 13,
    lineHeight: "32px",
    padding: 0,
  },
  "&::-webkit-calendar-picker-indicator": {
    width: 16,
    height: 16,
    margin: 0,
    padding: 0,
    cursor: "pointer",
  },
} as const;

export const FilterCriteriaEditor: React.FC<{
  dimensions: FilterDimension[];
  draft: FilterSelectionState;
  activeKey: string | null;
  onActiveKeyChange: (key: string) => void;
  onDraftChange: (next: FilterSelectionState) => void;
  optionsByKey: Record<string, FilterOption[]>;
  onReachEnd?: () => void;
  valuesLoading?: boolean;
  loadingMore?: boolean;
  leftEmptyMessage?: string | null;
  maxHeight?: number | string;
}> = ({
  dimensions,
  draft,
  activeKey,
  onActiveKeyChange,
  onDraftChange,
  optionsByKey,
  onReachEnd,
  valuesLoading = false,
  loadingMore = false,
  leftEmptyMessage = null,
  maxHeight = 320,
}) => {
  const valuesScrollerRef = useRef<HTMLDivElement | null>(null);
  const activeDimension =
    dimensions.find((dimension) => dimension.key === activeKey) ??
    dimensions[0] ??
    null;

  useEffect(() => {
    if (valuesScrollerRef.current) valuesScrollerRef.current.scrollTop = 0;
  }, [activeKey]);
  const activeSelected = activeDimension
    ? (draft.selections[activeDimension.key] ?? [])
    : [];
  const options = activeDimension
    ? pinSelectedOptions(
        optionsByKey[activeDimension.key] ?? [],
        activeSelected,
        "",
        {
          selection: activeDimension.selection,
          preserveSelectionOrder:
            activeDimension.key === FILTER_DIMENSION_KEYS.tags,
        },
      )
    : [];
  const customSelected =
    (draft.selections[FILTER_DIMENSION_KEYS.publishedDate] ?? [])[0]?.value ===
    "custom";

  const handleToggle = (option: FilterOption) => {
    if (!activeDimension) return;
    onDraftChange({
      ...draft,
      selections: {
        ...draft.selections,
        [activeDimension.key]: toggleFilterOption(
          draft.selections[activeDimension.key] ?? [],
          option,
          activeDimension.selection,
        ),
      },
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <Box
        role="listbox"
        aria-label="Filter dimensions"
        sx={{
          width: 200,
          flex: "none",
          borderRight: "1px solid rgba(0,0,0,0.06)",
          overflowY: "auto",
          maxHeight,
        }}
      >
        {leftEmptyMessage && (
          <Typography sx={{ p: 2, color: "#99A1AF", fontSize: 12 }}>
            {leftEmptyMessage}
          </Typography>
        )}
        {!leftEmptyMessage &&
          dimensions.map((dimension) => {
            const selected = dimension.key === activeDimension?.key;
            const count = dimensionSelectionCount(dimension.key, draft);
            return (
              <Button
                key={dimension.key}
                role="option"
                aria-selected={selected}
                fullWidth
                onClick={() => onActiveKeyChange(dimension.key)}
                endIcon={
                  <DimensionChevronIcon
                    sx={{
                      color: selected ? ACTIVE_BAR : "#A6A6A6",
                      fontSize: 12,
                      width: 12,
                      height: 12,
                    }}
                  />
                }
                sx={{
                  justifyContent: "space-between",
                  textTransform: "none",
                  borderRadius: 0,
                  px: "16px",
                  py: "12px",
                  minHeight: 42,
                  color: "#101828",
                  fontSize: 13,
                  fontWeight: 400,
                  bgcolor: selected ? ACTIVE_BG : "transparent",
                  position: "relative",
                  "&::before": selected
                    ? {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 3,
                        bgcolor: ACTIVE_BAR,
                      }
                    : undefined,
                  "&:hover": { bgcolor: selected ? ACTIVE_BG : "rgba(0,0,0,0.02)" },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    textAlign: "left",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: 13,
                      color: "#101828",
                    }}
                  >
                    {dimension.displayName}
                  </Box>
                  {count > 0 && (
                    <Box
                      component="span"
                      aria-label={`${count} selected`}
                      sx={{
                        minWidth: 23,
                        height: 19,
                        px: "6px",
                        borderRadius: "9.5px",
                        bgcolor: "rgba(41,94,241,0.06)",
                        color: ACTIVE_BAR,
                        fontSize: 12,
                        fontWeight: 600,
                        lineHeight: "19px",
                        textAlign: "center",
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </Box>
              </Button>
            );
          })}
      </Box>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          maxHeight,
          display: "flex",
          flexDirection: "column",
        }}
      >
      <Box
        ref={valuesScrollerRef}
        onScroll={(event) => {
          if (valuesLoading || loadingMore || !onReachEnd) return;
          const element = event.currentTarget;
          if (element.scrollHeight - element.scrollTop - element.clientHeight < 48) {
            onReachEnd();
          }
        }}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          maxHeight,
          px: "20px",
          py: "8px",
          ...(valuesLoading
            ? {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 160,
              }
            : null),
        }}
      >
        {valuesLoading ? (
          <CircularProgress size={22} aria-label="Loading filter values" />
        ) : null}
        {!valuesLoading &&
          !leftEmptyMessage &&
          activeDimension &&
          options.map((option) => {
            const checked = isFilterOptionSelected(option, activeSelected);
            const single = activeDimension.selection === "single";
            const showCustom =
              activeDimension.key === FILTER_DIMENSION_KEYS.publishedDate &&
              option.value === "custom" &&
              customSelected;
            return (
              <Box key={option.value}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleToggle(option)}
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    border: 0,
                    bgcolor: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    py: "11px",
                    px: 0,
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 15,
                      height: 15,
                      flex: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxSizing: "border-box",
                      border: `1.5px solid ${checked ? "#295EF1" : "#D1D5DC"}`,
                      background: single || !checked ? "#fff" : "#295EF1",
                      borderRadius: single ? "50%" : "2.5px",
                    }}
                  >
                    {single && checked && (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: "#295EF1",
                        }}
                      />
                    )}
                    {!single && checked && (
                      <OptionCheckIcon sx={{ fontSize: 9, width: 9, height: 9 }} />
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: "#101828" }}>
                    {option.name}
                  </Typography>
                </Box>
                {showCustom && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      pt: "2px",
                      pb: "12px",
                      pl: "25px",
                    }}
                  >
                    <Box
                      component="input"
                      type="date"
                      aria-label="From"
                      value={draft.customFrom}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onDraftChange({
                          ...draft,
                          customFrom: event.target.value,
                        })
                      }
                      sx={customDateInputSx}
                    />
                    <Box
                      component="span"
                      sx={{ fontSize: 13, color: "#6A7282", flex: "none", lineHeight: 1 }}
                    >
                      to
                    </Box>
                    <Box
                      component="input"
                      type="date"
                      aria-label="To"
                      value={draft.customTo}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onDraftChange({
                          ...draft,
                          customTo: event.target.value,
                        })
                      }
                      sx={customDateInputSx}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        {!valuesLoading &&
          !leftEmptyMessage &&
          activeDimension &&
          options.length === 0 && (
          <Typography sx={{ p: 2, color: "text.secondary", fontSize: 13 }}>
            No options
          </Typography>
        )}
      </Box>
        {!valuesLoading && loadingMore ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <CircularProgress size={22} aria-label="Loading more filter values" />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
